// Viagens: da saída da base até a volta, com as cargas e os vales de cada
// uma. Sem sinal, tudo funciona: grava no aparelho e sobe depois.
import {
  acertos, categorias, colaboradores, despesas, entregas, eventosDaViagem, fretes, licencas, parcelas, situacoesDeEnvio, vales, veiculos, viagens,
} from '../dados.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, numeroBR, placa as formatarPlaca, plural } from '../lib/formato.js';
import { cargasEmOrdem, paradasDaCarga, textoDaRota } from '../lib/entregas.js';
import { comContas, totaisDaViagem } from '../lib/fretes.js';
import { porVeiculo, situacaoDaPlaca } from '../lib/licencas.js';
import { fimDaViagem, inicioDaViagem } from '../lib/modo-viagem.js';
import { nestaTela } from '../rotas.js';
import { hoje as hojeNoFuso } from '../lib/datas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, dialogo, emblema, faixa, formulario, secao, vazio,
} from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { cartaoDespesa, consumos } from './despesas.js';
import { faixaDaConta } from './inicio.js';
import { blocoModoViagem } from './modo-viagem.js';

// Um momento (ISO) como o dia no fuso da empresa (AAAA-MM-DD).
function diaNoFuso(momento) {
  return hojeNoFuso(sessao.empresa?.fuso_horario, new Date(momento));
}

export const STATUS = {
  aberta: ['Em andamento', 'info'],
  encerrada: ['Encerrada', ''],
  acertada: ['Acertada', 'ok'],
  cancelada: ['Cancelada', ''],
};

export function nomeCurto(c) {
  return c ? c.apelido || c.nome.split(' ')[0] : 'Motorista';
}

export function textoCaminhao(v) {
  if (!v) return 'Caminhão';
  return `${formatarPlaca(v.placa)}${v.apelido ? ` · ${v.apelido}` : ''}`;
}

// Cartão de uma viagem (lista e tela inicial). `situacao`: a do caminhão
// agora ({ texto, estado }), de lib/entregas.js.
export function cartaoViagem(v, { frota, pessoas, cargas, envio, base = '#/viagens', situacao = null }) {
  const caminhao = frota.find((x) => x.id === v.veiculo_id);
  const motorista = pessoas.find((x) => x.id === v.motorista_id);
  const minhas = cargas.filter((f) => f.viagem_id === v.id);
  const bruto = minhas.reduce((s, f) => s + Number(f.valor_frete || 0), 0);
  const [texto, tipo] = STATUS[v.status] ?? [v.status, ''];
  return cartaoLink({
    href: `${base}/${v.id}`,
    simbolo: 'caminhao',
    titulo: `${textoCaminhao(caminhao)} · ${nomeCurto(motorista)}`,
    linhas: [
      situacao?.texto ?? null,
      v.data_chegada ? `De ${data(v.data_saida)} a ${data(v.data_chegada)}` : `Saiu em ${data(v.data_saida)}`,
      minhas.length > 0 ? `${plural(minhas.length, 'carga')} · ${dinheiro(bruto)}` : 'Nenhuma carga ainda',
    ],
    emblemas: [
      emblema(texto, tipo), situacao && emblema(situacao.estado === 'vazio' ? 'Vazio' : 'Carregado', situacao.estado === 'vazio' ? 'alerta' : 'info'),
      v.importacao_id && emblema('Programas antigos'), emblemaEnvio(envio?.get(v.id)),
    ],
  });
}

// ---------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------
export function telaViagens({ busca }) {
  const corpo = h('div', { class: 'secao' }, carregando());
  const quantas = Number(busca.get('ver')) || 20;

  (async () => {
    const [lista, frota, pessoas, cargas, envio] = await Promise.all([
      viagens.listar(), veiculos.listar(), colaboradores.listar(), fretes.listar(), situacoesDeEnvio('viagens'),
    ]);
    const gravavel = podeGravar();
    const extras = { frota, pessoas, cargas, envio };
    const abertas = lista.filter((v) => v.status === 'aberta');
    const outras = lista.filter((v) => v.status !== 'aberta');

    if (lista.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'caminhao',
        titulo: 'Nenhuma viagem ainda',
        texto: 'A viagem vai da saída da base até a volta, com quantas cargas houver. Os vales do motorista também ficam nela.',
        acao: gravavel && h('a', { class: 'botao', href: '#/viagens/nova' }, 'Começar uma viagem'),
      }));
      return;
    }

    trocar(corpo,
      gravavel && h('a', { class: 'botao', href: '#/viagens/nova' }, '+ Viagem'),
      secao('Em andamento', h('div', { class: 'lista' },
        abertas.map((v) => cartaoViagem(v, extras)),
        abertas.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma viagem em andamento.')), { contador: String(abertas.length) }),
      outras.length > 0 && secao('Encerradas', h('div', { class: 'lista' },
        outras.slice(0, quantas).map((v) => cartaoViagem(v, extras)),
        outras.length > quantas && h('a', { class: 'botao secundario', href: `#/viagens?ver=${quantas + 20}` }, 'Mostrar mais'))),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Viagens', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Começar ou editar a viagem
// ---------------------------------------------------------------------
export function telaViagemForm({ params, busca }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const voltar = nova ? '/viagens' : `/viagens/${params.id}`;

  (async () => {
    const [lista, frota, pessoas, lics] = await Promise.all([viagens.listar(), veiculos.listar(), colaboradores.listar(), licencas.listar()]);
    const v = nova ? null : lista.find((x) => x.id === params.id);
    const primeiraCarga = v ? (await fretes.daViagem(v.id))[0]?.data_carregamento ?? null : null;
    // Parte dela já está num acerto fechado (estava em andamento no acerto).
    const travada = Boolean(v) && (await acertos.partesDaViagem(v.id)).acertos.length > 0;
    const comTrava = (d) => (travada ? { ...d, veiculo_id: v.veiculo_id, motorista_id: v.motorista_id, data_saida: v.data_saida } : d);
    if (!nova && !v) {
      trocar(corpo, vazio({ titulo: 'Viagem não encontrada', acao: h('a', { class: 'botao', href: '#/viagens' }, 'Ver viagens') }));
      return;
    }
    const hoje = hojeDaEmpresa();
    const porV = porVeiculo(lics);
    const tratores = frota.filter((x) => x.tipo === 'trator' && (x.ativo || x.id === v?.veiculo_id));
    const motoristas = pessoas.filter((p) => p.funcao === 'motorista' && (p.ativo || p.id === v?.motorista_id));

    if (tratores.length === 0 || motoristas.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'caminhao',
        titulo: tratores.length === 0 ? 'Cadastre um caminhão primeiro' : 'Cadastre um motorista primeiro',
        texto: 'A viagem é sempre de um caminhão com um motorista.',
        acao: h('a', { class: 'botao', href: tratores.length === 0 ? '#/veiculos/novo?tipo=trator' : '#/colaboradores/novo' },
          tratores.length === 0 ? 'Cadastrar caminhão' : 'Cadastrar motorista'),
      }));
      return;
    }

    // Sugestões: o caminhão pedido (ou o único) e o motorista da última viagem dele.
    const caminhaoSugerido = v?.veiculo_id ?? busca.get('caminhao') ?? (tratores.length === 1 ? tratores[0].id : '');
    const ultimoMotorista = (caminhaoId) => lista.find((x) => x.veiculo_id === caminhaoId)?.motorista_id ?? '';
    const motoristaSugerido = v?.motorista_id ?? (motoristas.length === 1 ? motoristas[0].id : ultimoMotorista(caminhaoSugerido));

    const form = formulario({
      filhos: [
        travada && faixa('Parte desta viagem já entrou num acerto fechado: o caminhão, o motorista e a saída não mudam mais.', 'info'),
        campo({
          rotulo: 'Caminhão',
          nome: 'veiculo_id',
          tipo: 'select',
          obrigatorio: true,
          desativado: travada,
          valor: caminhaoSugerido,
          opcoes: [['', 'Escolha o caminhão'], ...tratores.map((t) => [t.id, textoCaminhao(t)])],
          aoMudar: (e) => {
            const m = form.querySelector('[name="motorista_id"]');
            const ultimo = ultimoMotorista(e.target.value);
            if (nova && ultimo) m.value = ultimo;
          },
        }),
        campo({
          rotulo: 'Motorista',
          nome: 'motorista_id',
          tipo: 'select',
          obrigatorio: true,
          desativado: travada,
          valor: motoristaSugerido,
          opcoes: [['', 'Escolha o motorista'], ...motoristas.map((m) => [m.id, m.apelido ? `${m.nome} (${m.apelido})` : m.nome])],
        }),
        h('div', { class: 'grade-2' },
          campo({ rotulo: 'Saída da base', nome: 'data_saida', tipo: 'date', obrigatorio: true, desativado: travada, valor: v?.data_saida ?? hoje }),
          campo({
            rotulo: 'Km na saída',
            nome: 'km_saida',
            formato: 'km',
            valor: v?.km_saida != null ? numeroBR(v.km_saida) : '',
            dica: 'Opcional.',
            atributos: { inputmode: 'numeric' },
          })),
        campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: v?.observacao ?? '' }),
      ],
      textoBotao: nova ? 'Começar viagem' : 'Salvar alterações',
      somenteLeitura: !podeGravar(),
      validar: (lidos) => {
        const d = comTrava(lidos);
        const erros = {};
        if (!d.veiculo_id) erros.veiculo_id = 'Escolha o caminhão.';
        if (!d.motorista_id) erros.motorista_id = 'Escolha o motorista.';
        if (!d.data_saida) erros.data_saida = 'Informe a data de saída.';
        if (d.km_saida && !(lerInteiro(d.km_saida) >= 0)) erros.km_saida = 'Só números.';
        if (d.veiculo_id && d.data_saida) {
          if (!situacaoDaPlaca(porV[d.veiculo_id] ?? [], d.data_saida).ativa) {
            erros.veiculo_id = `A placa deste caminhão não estava ativa em ${data(d.data_saida)}. Ative a placa em Veículos.`;
          }
          // O mesmo caminhão não faz duas viagens ao mesmo tempo.
          const outras = lista.filter((x) => x.veiculo_id === d.veiculo_id && x.id !== v?.id && x.status !== 'cancelada');
          const aberta = outras.find((x) => x.status === 'aberta');
          const cruzada = outras.find((x) => x.data_saida <= d.data_saida && (!x.data_chegada || x.data_chegada > d.data_saida));
          if (nova && aberta) erros.veiculo_id = `Este caminhão já está em viagem desde ${data(aberta.data_saida)}. Encerre aquela antes.`;
          else if (cruzada) erros.data_saida = `Este caminhão estava na viagem de ${data(cruzada.data_saida)}${cruzada.data_chegada ? ` a ${data(cruzada.data_chegada)}` : ''}.`;
        }
        if (v?.data_chegada && d.data_saida > v.data_chegada) erros.data_saida = `A saída precisa ser antes da chegada (${data(v.data_chegada)}).`;
        if (primeiraCarga && d.data_saida > primeiraCarga) erros.data_saida = `Há carga carregada em ${data(primeiraCarga)}: a saída não pode ser depois disso.`;
        return erros;
      },
      aoEnviar: async (lidos) => {
        const d = comTrava(lidos);
        const dados = {
          veiculo_id: d.veiculo_id,
          motorista_id: d.motorista_id,
          data_saida: d.data_saida,
          km_saida: d.km_saida ? lerInteiro(d.km_saida) : null,
          observacao: d.observacao || null,
        };
        if (nova) {
          const criada = await viagens.criar(dados);
          avisar('Viagem começada. Agora lance as cargas.');
          tela.ir(`/viagens/${criada.id}`, { substituir: true });
        } else {
          await viagens.atualizar(v.id, dados, v);
          avisar('Alterações salvas.');
          tela.ir(`/viagens/${v.id}`, { substituir: true });
        }
      },
    });

    trocar(corpo, form);
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: nova ? 'Nova viagem' : 'Dados da viagem', voltar, conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// A viagem: cargas, vales, resumo, encerrar
// ---------------------------------------------------------------------
export function telaViagem({ params }) {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, 'Viagem');

  (async () => {
    const v = await viagens.obter(params.id);
    if (!v) {
      trocar(corpo, vazio({ titulo: 'Viagem não encontrada', texto: 'Ela pode ter sido excluída.', acao: h('a', { class: 'botao', href: '#/viagens' }, 'Ver viagens') }));
      return;
    }
    const [frota, pessoas, cargas, valesDela, despesasDela, cats, parc, leituras, envioCargas, envioVales, envioViagem, todasParadas] = await Promise.all([
      veiculos.listar(), colaboradores.listar(), fretes.daViagem(v.id), vales.daViagem(v.id), despesas.daViagem(v.id),
      categorias.todas(), parcelas.listar(), despesas.leituras(),
      situacoesDeEnvio('fretes'), situacoesDeEnvio('lancamentos'), situacoesDeEnvio('viagens'), entregas.listar(),
    ]);
    const hoje = hojeDaEmpresa();
    const porConsumo = consumos(leituras, cats);
    const acerto = v.acerto_id ? await acertos.obter(v.acerto_id) : null;
    // Viagem que estava em andamento num acerto: parte dela já foi acertada.
    const partes = await acertos.partesDaViagem(v.id);
    const parcial = v.status !== 'acertada' && partes.acertos.length > 0;
    const caminhao = frota.find((x) => x.id === v.veiculo_id);
    const motorista = pessoas.find((x) => x.id === v.motorista_id);
    titulo.textContent = `Viagem · ${formatarPlaca(caminhao?.placa ?? '')}`;
    const gravavel = podeGravar() && v.status !== 'acertada';
    const t = totaisDaViagem(cargas, valesDela);
    const [statusTexto, statusTipo] = STATUS[v.status] ?? [v.status, ''];

    const cabecalho = h('div', { class: 'cartao destaque' },
      h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${textoCaminhao(caminhao)}`),
        h('span', { class: 'cartao-linha' }, `Motorista: ${motorista ? motorista.nome : '—'}`),
        h('span', { class: 'cartao-linha' },
          `Saída: ${data(v.data_saida)}${v.km_saida != null ? ` · km ${numeroBR(v.km_saida)}` : ''}`),
        v.data_chegada && h('span', { class: 'cartao-linha' },
          `Chegada: ${data(v.data_chegada)}${v.km_chegada != null ? ` · km ${numeroBR(v.km_chegada)}` : ''}${v.km_chegada != null && v.km_saida != null ? ` (${numeroBR(v.km_chegada - v.km_saida)} km rodados)` : ''}`),
        v.observacao && h('span', { class: 'cartao-linha' }, v.observacao),
        h('span', { class: 'cartao-emblemas' }, emblema(statusTexto, statusTipo), emblemaEnvio(envioViagem.get(v.id)))));

    const somar = (lista) => lista.reduce((s, l) => s + Math.round(Number(l.valor) * 100), 0) / 100;
    const gastos = somar(despesasDela);
    const doMotorista = somar(despesasDela.filter((l) => l.pago_por === 'colaborador'));
    const resumo = h('dl', { class: 'linha-dados resumo' },
      h('dt', null, 'Frete bruto'), h('dd', null, dinheiro(t.bruto)),
      h('dt', null, 'Frete líquido'), h('dd', null, dinheiro(t.liquido)),
      h('dt', null, 'Adiantamentos'), h('dd', null, dinheiro(t.adiantamentos)),
      h('dt', null, 'Saldo a receber'), h('dd', null, dinheiro(t.aReceber)),
      h('dt', null, 'Vales ao motorista'), h('dd', null, dinheiro(t.vales)),
      h('dt', null, 'Despesas da viagem'), h('dd', null, dinheiro(gastos)),
      doMotorista > 0 && [h('dt', null, 'Pagas pelo motorista'), h('dd', null, dinheiro(doMotorista))]);

    // Cada carga principal seguida dos complementos dela.
    const cartoesCargas = cargasEmOrdem(cargas).map((f) => {
      const c = comContas(f);
      const paradasDela = paradasDaCarga(todasParadas, f.id);
      const rota = textoDaRota(f, paradasDela);
      return cartaoLink({
        href: `#/cargas/${f.id}`,
        simbolo: 'carreta',
        titulo: `${f.complemento_de ? 'Complemento · ' : ''}${dinheiro(f.valor_frete)} · ${data(f.data_carregamento)}`,
        linhas: [rota || null, f.saldo_recebido_em ? 'Saldo recebido' : `A receber: ${dinheiro(c.saldo_valor)}`],
        emblemas: [
          f.data_descarga ? emblema('Entregue', 'ok') : f.data_carregamento <= hoje && emblema('No caminhão', 'info'),
          paradasDela.length > 0 && emblema(paradasDela.length === 1 ? '1 entrega no caminho' : `${paradasDela.length} entregas no caminho`),
          f.acerto_id && v.status !== 'acertada' && emblema('Acertada', 'ok'),
          f.agenciado_por === 'transportadora' && !f.agenciamento_pago_em && Number(f.agenciamento_valor) > 0 && emblema('Agenciamento a pagar', 'alerta'),
          emblemaEnvio(envioCargas.get(f.id)),
        ],
      });
    });

    const cartoesVales = valesDela.map((l) => cartaoLink({
      href: `#/vales/${l.id}`,
      simbolo: 'dinheiro',
      titulo: `${dinheiro(l.valor)} · ${data(l.data)}`,
      linhas: [nomeCurto(pessoas.find((p) => p.id === l.colaborador_id))],
      emblemas: [l.assinatura ? emblema('Assinado', 'ok') : emblema('Sem assinatura'), emblemaEnvio(envioVales.get(l.id))],
    }));

    trocar(corpo,
      v.status === 'acertada' && (v.importacao_id
        ? faixa('Esta viagem veio dos programas antigos e já foi acertada neles. Na carga, dá para registrar o recebimento, o comprovante e os dados da entrega.', 'info')
        : faixa('Esta viagem já foi acertada: nada nela muda mais (só o titular desfaz o acerto).', 'info')),
      parcial && faixa(
        `Parte desta viagem já entrou no acerto ${partes.acertos.map((a) => `nº ${a.numero}/${a.ano} (até ${data(a.data)})`).join(' e no ')}. `
        + 'As cargas desse acerto não mudam mais; o que for lançado depois entra no próximo.', 'info'),
      cabecalho,
      // Modo viagem (0.23.0): o próximo passo, a rota, os gastos e, no fim, o previsto e o realizado.
      blocoModoViagem(v, { aoMudar: () => tela.renderizar() }),
      secao('Cargas', h('div', { class: 'lista' },
        cartoesCargas,
        cargas.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma carga lançada.'),
        gravavel && h('a', { class: 'botao', href: `#/viagens/${v.id}/cargas/nova` }, '+ Carga')), { contador: String(cargas.length) }),
      secao('Vales', h('div', { class: 'lista' },
        cartoesVales,
        valesDela.length === 0 && h('p', { class: 'pequeno' }, 'Nenhum vale nesta viagem.'),
        gravavel && h('a', { class: 'botao secundario', href: `#/vales/novo?viagem=${v.id}` }, '+ Vale')), { contador: String(valesDela.length) }),
      secao('Despesas', h('div', { class: 'lista' },
        despesasDela.map((l) => cartaoDespesa(l, { cats, frota, pessoas, parc, envio: envioVales, consumo: porConsumo.get(l.id), sufixo: `?viagem=${v.id}` })),
        despesasDela.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma despesa nesta viagem.'),
        gravavel && h('div', { class: 'acoes-lado' },
          h('a', { class: 'botao secundario', href: `#/despesas/nova?tipo=diesel&viagem=${v.id}` }, '+ Diesel'),
          h('a', { class: 'botao secundario', href: `#/despesas/nova?viagem=${v.id}` }, '+ Despesa'))), { contador: String(despesasDela.length) }),
      secao('Resumo', resumo),
      acerto && h('a', { class: 'botao secundario', href: `#/acertos/${acerto.id}` }, `Ver o acerto nº ${acerto.numero}/${acerto.ano}`),
      partes.acertos.filter((a) => a.id !== acerto?.id).map((a) => h('a', { class: 'botao secundario', href: `#/acertos/${a.id}` },
        `Ver o acerto nº ${a.numero}/${a.ano}`)),
      (v.status === 'encerrada' || v.status === 'aberta') && podeGravar() && motorista
        && h('a', { class: v.status === 'encerrada' ? 'botao' : 'botao secundario', href: `#/acertos/novo?colaborador=${motorista.id}` },
          `Fazer o acerto de ${nomeCurto(motorista)}`),
      gravavel && acoesDaViagem(v, cargas, tela, cargas.length + valesDela.length + despesasDela.length > 0, { parcial, pagaAte: partes.pagaAte }),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: '/viagens', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

function acoesDaViagem(v, cargas, tela, temLancamentos, { parcial = false, pagaAte = null } = {}) {
  const executar = async (acao, sucesso, depois = null) => {
    try {
      await acao();
      avisar(sucesso);
      if (depois) tela.ir(depois, { substituir: true });
      else tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  const encerrar = async () => {
    // O que o motorista disse ao encerrar no app dele (modo viagem) já vem preenchido.
    const eventos = await eventosDaViagem.daViagem(v.id).catch(() => []);
    const fim = fimDaViagem(eventos);
    const r = await dialogoEncerrar(v, cargas, pagaAte, fim);
    if (!r) return;
    executar(async () => {
      await viagens.atualizar(v.id, { ...r, status: 'encerrada' });
      // Viagem iniciada no modo viagem, sem o "Encerrar" do motorista: fica o
      // do escritório (com a hora de agora, ou aproximada quando a chegada
      // foi em outro dia). Viagem sem o "Iniciar": nada a anotar.
      if (!fim && inicioDaViagem(eventos)) {
        const hoje = hojeDaEmpresa();
        const agora = r.data_chegada === hoje;
        await eventosDaViagem.criar({
          viagem_id: v.id, tipo: 'fim', km: r.km_chegada ?? null,
          momento: agora ? new Date().toISOString() : new Date(`${r.data_chegada}T12:00:00`).toISOString(),
          dados: agora ? null : { aproximado: true },
        }, { esperarServidor: false });
      }
    }, 'Viagem encerrada.');
  };
  const reabrir = async () => {
    const ok = await confirmar({ titulo: 'Reabrir a viagem?', texto: 'Ela volta para "Em andamento" e a data de chegada é apagada.', sim: 'Reabrir' });
    if (ok) executar(() => viagens.atualizar(v.id, { status: 'aberta', data_chegada: null, km_chegada: null }), 'Viagem reaberta.');
  };
  const excluir = async () => {
    const ok = await confirmar({
      titulo: 'Excluir esta viagem?',
      texto: temLancamentos
        ? 'As cargas, os vales e as despesas dela também vão para a lixeira. Use só para viagem lançada por engano.'
        : 'Use só para viagem lançada por engano. Ela vai para a lixeira.',
      sim: 'Excluir',
      perigo: true,
    });
    if (ok) executar(() => viagens.excluir(v.id), 'Viagem excluída.', '/viagens');
  };

  return h('div', { class: 'acoes' },
    v.status === 'aberta' && h('button', { class: 'botao cheio', type: 'button', onClick: encerrar }, 'Encerrar viagem (voltou à base)'),
    v.status === 'encerrada' && h('button', { class: 'botao cheio secundario', type: 'button', onClick: reabrir }, 'Reabrir viagem'),
    h('a', { class: 'botao cheio secundario', href: `#/viagens/${v.id}/editar` }, 'Editar dados da viagem'),
    !parcial && h('button', { class: 'botao texto', type: 'button', onClick: excluir }, 'Excluir viagem lançada por engano'));
}

function dialogoEncerrar(v, cargas, pagaAte = null, fim = null) {
  const hoje = hojeDaEmpresa();
  // A chegada não fica antes da última carga nem da última diária já paga.
  const ultimaCarga = [...cargas.map((f) => f.data_carregamento), pagaAte].filter(Boolean)
    .reduce((m, d) => (d > m ? d : m), v.data_saida);
  // O dia em que o motorista encerrou no app (no fuso da empresa).
  const diaDoFim = fim ? diaNoFuso(fim.momento) : null;
  const sugerida = diaDoFim && diaDoFim >= ultimaCarga ? diaDoFim : hoje < ultimaCarga ? ultimaCarga : hoje;
  return dialogo((fechar) => {
    const chegada = h('input', { type: 'date', id: 'encerrar-chegada', value: sugerida, min: ultimaCarga });
    const km = h('input', {
      type: 'text', id: 'encerrar-km', inputmode: 'numeric', value: fim?.km ? numeroBR(fim.km) : '',
      placeholder: v.km_saida != null ? `Saiu com ${numeroBR(v.km_saida)}` : 'Opcional',
    });
    km.addEventListener('input', () => {
      const d = km.value.replace(/\D/g, '').slice(0, 8);
      km.value = d.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, 'Encerrar a viagem'),
      h('p', null, fim ? 'O motorista já encerrou no app dele: confira e confirme.' : 'Quando o caminhão voltou para a base?'),
      h('div', { class: 'campo' }, h('label', { for: 'encerrar-chegada' }, 'Chegada na base'), chegada),
      h('div', { class: 'campo' }, h('label', { for: 'encerrar-km' }, 'Km na chegada'), km),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            const kmChegada = km.value ? Number(km.value.replace(/\D/g, '')) : null;
            if (!chegada.value) {
              erro.textContent = 'Informe a data de chegada.';
              return;
            }
            if (chegada.value < ultimaCarga) {
              const motivo = pagaAte && ultimaCarga === pagaAte ? 'diárias já pagas até esse dia' : cargas.length > 0 ? 'última carga' : 'saída';
              erro.textContent = `A chegada não pode ser antes de ${data(ultimaCarga)} (${motivo}).`;
              return;
            }
            if (kmChegada !== null && v.km_saida != null && kmChegada < v.km_saida) {
              erro.textContent = `O km da chegada não pode ser menor que o da saída (${numeroBR(v.km_saida)}).`;
              return;
            }
            fechar({ data_chegada: chegada.value, km_chegada: kmChegada });
          },
        }, 'Encerrar viagem'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}
